import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, Users, Shield, TrendingUp, Clock } from 'lucide-react';

const Guides = () => {
  const guideTopics = [
    {
      title: 'Mastering Online Casino Play in the UK',
      description: 'Navigate the exciting world of UK online gaming with confidence. Learn about licensing, responsible play, and maximizing your entertainment.',
      icon: <BookOpen className="h-6 w-6" />,
      difficulty: 'Beginner',
      route: '/guides/choosing-casino'
    },
    {
      title: 'Understanding Casino Bonuses & Promotions',
      description: 'Demystify welcome bonuses, free spins, and loyalty rewards. Discover how to turn casino incentives into real gaming advantages.',
      icon: <TrendingUp className="h-6 w-6" />,
      difficulty: 'Intermediate',
      route: '/guides/bonuses-promotions'
    },
    {
      title: 'Responsible Gaming Strategies',
      description: 'Play smarter, not harder. Essential tips for maintaining control, setting limits, and ensuring gaming remains an enjoyable pastime.',
      icon: <Shield className="h-6 w-6" />,
      difficulty: 'All Levels',
      route: '/guides/responsible-gaming'
    },
    {
      title: 'Payment Methods & Security',
      description: 'Explore secure deposit and withdrawal options available to UK players. Understand fees, processing times, and protecting your funds.',
      icon: <Users className="h-6 w-6" />,
      difficulty: 'Beginner',
      route: '/guides/payment-methods'
    }
  ];

  return (
    <section id="guides" className="book-section bg-book-beige">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
            Expert Gaming Guides
          </h2>
          <p className="text-xl text-book-brown-dark max-w-3xl mx-auto">
            Transform your gaming experience with our comprehensive guides, crafted by industry veterans
            who understand what it takes to succeed in the world of online casinos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {guideTopics.map((guide, index) => (
            <Link
              key={index}
              to={guide.route}
              data-discover="true"
              className="bg-white rounded-xl shadow-book p-6 hover:shadow-lg transition-shadow duration-200 block"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center text-book-cream">
                    {guide.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-book-brown">{guide.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      guide.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
                      guide.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {guide.difficulty}
                    </span>
                  </div>
                  <p className="text-book-brown-dark mb-4 leading-relaxed">
                    {guide.description}
                  </p>
                  <div className="flex items-center text-book-brown-light hover:text-book-brown transition-colors duration-200">
                    <span className="font-medium">Read Guide</span>
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="bg-gradient-to-r from-book-brown to-book-brown-light rounded-xl p-8 text-center text-book-cream">
          <Clock className="h-12 w-12 mx-auto mb-4 opacity-80" />
          <h3 className="text-book-cream text-2xl font-bold mb-4">Ready to Level Up Your Gaming?</h3>
          <p className="text-lg mb-6 opacity-90 max-w-2xl mx-auto">
            Our comprehensive guides cover everything from beginner basics to advanced strategies.
            Whether you're new to online casinos or looking to refine your approach, we have the insights you need.
          </p>
          <Link
            to="/guides"
            data-discover="true"
            className="inline-flex items-center space-x-2 bg-book-gold text-book-brown px-8 py-3 rounded-lg hover:bg-book-beige transition-colors duration-200 font-semibold"
          >
            <span>Explore All Guides</span>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Guides;
